/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { HistoryStacks } from 'src/core/types/HistoryStacks';
import { getStorageUrl } from 'src/core/utils/api/storageUtils';
import { CustomTextDocumentStore } from 'src/customText/core/stores/CustomTextDocumentStore';
import { CustomTextEntityLabelStore } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { ProjectDomain } from 'src/customText/core/stores/CustomTextProjectStore';
import { CustomTextVerificationDocumentStore } from 'src/customText/core/stores/CustomTextVerificationDocumentStore';
import { getBrowserLocalDocumentStorage } from 'src/customText/core/utils/documentStorage/storageProviders/browserLocalDocumentStorage';
import { getAzureDocumentStorageService } from 'src/customText/core/utils/documentStorage/storageServices/azureDocumentStorageService';
import { getLruCachedDocumentStorage } from 'src/customText/core/utils/documentStorage/storageServices/lruCachedDocumentStorageService';
import { isDocumentTagged } from 'src/customText/core/utils/documentUtils';
import { validateGptPredictionWithUserLabel } from 'src/customText/modules/handlers/customTextGptHandlerGroup/gptMappers';
import { documentMetadataToDocumentStore, documentStoreToDocumentMetadata } from 'src/customText/modules/handlers/projects/projectMappers';
import { PartialEntityLabel } from 'src/customText/verticals/extractionLabeler/types';
import { labelingInteractions, LabelingInteractionsAction } from 'src/luis/projects/utterances/handlers/labelingInteractions';
import { LabelingInteractionsEntityLabel } from 'src/luis/projects/utterances/handlers/labelingInteractionsUtils';
import { getLabelId } from 'src/modules/gptPrediction/utils/gptUtils';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';

export type CustomTextDocumentHandlerGroup = ReturnType<typeof createCustomTextDocumentHandlerGroup>;

const fromInteractionsLabelToCTLabel = (label: LabelingInteractionsEntityLabel) =>
    new CustomTextEntityLabelStore({
        entityId: label.entityId.toString(),
        startCharIndex: label.start,
        endCharIndex: label.end,
        labelType: label.labelType
    });

const fromCTLabelToInteractionsLabel = (label: CustomTextEntityLabelStore): LabelingInteractionsEntityLabel => ({
    entityId: label.entityId,
    start: label.startCharIndex,
    end: label.endCharIndex,
    labelType: label.labelType
});

const isSubmittedLabel = (action: LabelingInteractionsAction, labelToCheck: CustomTextEntityLabelStore) => {
    if (action.name === 'ConfirmPredictions' || action.name === 'RemoveLabels') {
        return action.labels.find(
            sL => sL.entityId === labelToCheck.entityId && sL.start === labelToCheck.startCharIndex && sL.end === labelToCheck.endCharIndex
        );
    }

    if (action.name === 'ResizeLabel' || action.name === 'AddLabel' || action.name === 'ResizePrediction') {
        return (
            action.label.entityId === labelToCheck.entityId &&
            action.label.start === labelToCheck.startCharIndex &&
            action.label.end === labelToCheck.endCharIndex
        );
    }

    return false;
};

/**
 * Represents a handler group for custom text document operations.
 *
 * @param dependencies The handler group dependencies.
 */
export const createCustomTextDocumentHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const getDocumentStorageServices = (containerUrl: string, storageToken: string) => {
        const documentStorage = getBrowserLocalDocumentStorage({
            containerId: containerUrl,
            storageType: 'sessionStorage'
        });
        const azureDocumentStorageService = getAzureDocumentStorageService({
            tenantToken: storageToken,
            containerUrl
        });

        return getLruCachedDocumentStorage(azureDocumentStorageService, documentStorage);
    };
    const _addDirtyState = (domain?: ProjectDomain) => rootStore.currentCustomTextProjectStore.setIsDirty(true, domain);

    const fetchDocumentContentByName = async ({
        documentName,
        localStore
    }: HandlerParams<{
        documentName: string;
        localStore?: CustomTextVerificationDocumentStore | CustomTextDocumentStore;
    }>) => {
        const {
            currentCustomTextProjectStore,
            azureStore: { session }
        } = rootStore;
        const { storageToken } = rootStore.azureStore.session.userTokens;

        const storageAccountName = session.csAccountStore?.ownedStorageAccountInfo.name;

        const documentStore = localStore ?? currentCustomTextProjectStore.currentDocumentStore;

        const containerUrl = getStorageUrl({ storageAccountName, containerName: currentCustomTextProjectStore.containerName });
        const content = await getDocumentStorageServices(containerUrl, storageToken).getDocument(documentName);

        if (!documentStore.isInitialized) {
            documentStore.initialize(content);
        }
    };

    const addNewExtractionEntity = async ({ entityName }: HandlerParams<{ entityName: string }>) => {
        const { extractionStore } = rootStore.currentCustomTextProjectStore;

        if (extractionStore.entities.find(e => e.name === entityName)) {
            throw new Error('The entity name already exists.');
        }

        transaction(() => {
            extractionStore.addEntity(entityName);
            _addDirtyState();
        });
    };

    const addNewClassificationClass = async ({ className }: HandlerParams<{ className: string }>) => {
        const { classificationStore } = rootStore.currentCustomTextProjectStore;

        if (classificationStore.classes.find(cls => cls.name === className)) {
            throw new Error('The class name already exists.');
        }

        transaction(() => {
            classificationStore.addClass(className);
            _addDirtyState();
        });
    };

    const removeExtractionEntities = async ({ entitiesIds }: HandlerParams<{ entitiesIds: string[] }>) => {
        const { extractionStore, documents } = rootStore.currentCustomTextProjectStore;
        transaction(() => {
            extractionStore.deleteEntitiesByIds(entitiesIds);
            const updatedDocuments = documents.map(doc => {
                const entityLabels = doc.entityLabels.filter(eL => !entitiesIds.includes(eL.entityId));
                return isDocumentTagged(doc)
                    ? {
                          ...doc,
                          entityLabels,
                          documentDataset: entityLabels.length > 0 ? doc.documentDataset : null
                      }
                    : { ...doc, documentDataset: null };
            });

            _addDirtyState();
            rootStore.currentCustomTextProjectStore.setProjectDocuments(updatedDocuments);
            rootStore.currentCustomTextProjectStore.currentDocumentStore.deleteEntityLabels(entitiesIds);
        });
    };

    const removeClassificationClasses = async ({ classesIds }: HandlerParams<{ classesIds: string[] }>) => {
        const { classificationStore, documents } = rootStore.currentCustomTextProjectStore;
        transaction(() => {
            classificationStore.deleteClassesByIds(classesIds);
            const updatedDocuments = documents.map(doc => {
                const classLabels = doc.classLabels.filter(cL => !classesIds.includes(cL.id));
                return isDocumentTagged(doc)
                    ? {
                          ...doc,
                          classLabels,
                          documentDataset: classLabels.length === 0 ? null : doc.documentDataset
                      }
                    : doc;
            });
            rootStore.currentCustomTextProjectStore.setProjectDocuments(updatedDocuments);
            rootStore.currentCustomTextProjectStore.currentDocumentStore.deleteClassLabels(classesIds);

            _addDirtyState('documents');
        });
    };

    const redoExtractionLabels = async ({
        labelingHistoryStacks
    }: HandlerParams<{
        labelingHistoryStacks: HistoryStacks<CustomTextEntityLabelStore[]>;
    }>) => {
        const { currentDocumentStore } = rootStore.currentCustomTextProjectStore;

        const lastLabels = labelingHistoryStacks.redo.pop();

        if (lastLabels) {
            transaction(() => {
                labelingHistoryStacks.undo.push(currentDocumentStore.entityLabels);
                currentDocumentStore.setEntityLabels(lastLabels);
                _addDirtyState();
            });
        }
    };

    const undoExtractionLabels = async ({
        labelingHistoryStacks
    }: HandlerParams<{
        labelingHistoryStacks: HistoryStacks<CustomTextEntityLabelStore[]>;
    }>) => {
        const { currentDocumentStore } = rootStore.currentCustomTextProjectStore;
        const lastLabels = labelingHistoryStacks.undo.pop();

        if (lastLabels) {
            transaction(() => {
                labelingHistoryStacks.redo.push(currentDocumentStore.entityLabels);
                currentDocumentStore.setEntityLabels(lastLabels);
                _addDirtyState();
            });
        }
    };

    const submitExtractionLabels = async ({
        action,
        markDirty = true,
        labelingHistoryStacks
    }: HandlerParams<{
        markDirty?: boolean;
        action: LabelingInteractionsAction;
        labelingHistoryStacks: HistoryStacks<CustomTextEntityLabelStore[]>;
    }>) => {
        const {
            documents,
            gptPredictionsStore,
            currentDocumentStore,
            extractionStore: { entities }
        } = rootStore.currentCustomTextProjectStore;

        let newLabels: CustomTextEntityLabelStore[] = [];

        if (action.name === 'ConfirmPredictions') {
            newLabels = currentDocumentStore.entityLabels.map(entityLabel => {
                if (
                    action.labels.find(
                        dL =>
                            dL.entityId === entityLabel.entityId &&
                            dL.start === entityLabel.startCharIndex &&
                            dL.end === entityLabel.endCharIndex
                    )
                ) {
                    return { ...entityLabel, labelType: 'normal' };
                }
                return entityLabel;
            });
        } else {
            newLabels = labelingInteractions(
                {
                    entities,
                    labels: currentDocumentStore.entityLabels.map(fromCTLabelToInteractionsLabel)
                },
                action,
                {
                    mutualExclusiveTopLevel: true
                }
            ).labels.map(fromInteractionsLabelToCTLabel);
        }

        transaction(() => {
            const labelsToSet = newLabels.map(l => ({
                ...l,
                autoTagged:
                    !isSubmittedLabel(action, l) &&
                    currentDocumentStore.entityLabels.find(
                        e => e.entityId === l.entityId && e.startCharIndex === l.startCharIndex && e.endCharIndex === l.endCharIndex
                    )?.labelType === 'autoTagged'
            }));
            labelingHistoryStacks.undo.push(currentDocumentStore.entityLabels);
            currentDocumentStore.setEntityLabels(labelsToSet);

            if (!currentDocumentStore.documentDataset && newLabels.length > 0) {
                currentDocumentStore.setDocumentDataSet('Train');
            }

            if (currentDocumentStore.documentDataset && newLabels.length === 0) {
                currentDocumentStore.setDocumentDataSet(null);
            }

            labelingHistoryStacks.redo = [];

            const documentInProject = documents.find(d => d.name === currentDocumentStore.name);
            const doesDocumentAlreadyExists = Boolean(documentInProject);

            if (doesDocumentAlreadyExists) {
                rootStore.currentCustomTextProjectStore.setProjectDocuments(
                    documents.map(doc =>
                        currentDocumentStore.name === doc.name
                            ? {
                                  ...doc,
                                  entityLabels: labelsToSet,
                                  documentDataset: labelsToSet.length ? doc.documentDataset || 'Train' : null
                              }
                            : doc
                    )
                );
            } else {
                rootStore.currentCustomTextProjectStore.setProjectDocuments([
                    ...documents,
                    {
                        ...currentDocumentStore,
                        entityLabels: labelsToSet,
                        documentDataset: labelsToSet.length ? currentDocumentStore.documentDataset || 'Train' : null
                    }
                ]);
            }

            const gptDocument = gptPredictionsStore.documents.find(d => d.name === currentDocumentStore.name);
            if (action.name === 'AddLabel' && gptDocument) {
                const conflictingEntity = gptDocument.entityLabels.find(l =>
                    validateGptPredictionWithUserLabel(
                        {
                            offset: l.startCharIndex,
                            length: l.endCharIndex - l.startCharIndex + 1,
                            category: entities.find(e => e.id === l.entityId).name
                        },
                        { endCharIndex: action.label.end, startCharIndex: action.label.start, entityId: action.label.entityId }
                    )
                );

                if (conflictingEntity) {
                    rejectGptLabels({ labels: [conflictingEntity] });
                }
            }

            if (markDirty) {
                _addDirtyState();
            }
        });
    };

    const submitClassificationClasses = async ({
        classIds,
        documentNames
    }: HandlerParams<{
        classIds: string[];
        documentNames?: string[];
    }>) => {
        const {
            classificationStore: { classes },
            documents
        } = rootStore.currentCustomTextProjectStore;

        const foundLabels = classIds.map(cId => classes.find(cls => cls.id === cId));

        const updatedDocuments = documents.map(doc =>
            documentNames.includes(doc.name)
                ? { ...doc, classLabels: foundLabels, documentDataset: foundLabels.length > 0 ? doc.documentDataset || 'Train' : null }
                : doc
        );

        transaction(() => {
            rootStore.currentCustomTextProjectStore.setProjectDocuments(updatedDocuments);
            _addDirtyState('documents');
        });
    };

    const submitCurrentDocumentClassificationClasses = async ({
        classIds
    }: HandlerParams<{
        classIds: string[];
    }>) => {
        const {
            classificationStore: { classes },
            currentDocumentStore,
            documents
        } = rootStore.currentCustomTextProjectStore;
        const foundLabels = classIds.map(cId => classes.find(cls => cls.id === cId));

        transaction(() => {
            currentDocumentStore.setClassLabels(foundLabels);

            const documentInProject = documents.find(d => d.name === currentDocumentStore.name);
            const doesDocumentAlreadyExists = Boolean(documentInProject);

            if (doesDocumentAlreadyExists) {
                rootStore.currentCustomTextProjectStore.setProjectDocuments(
                    documents.map(doc =>
                        currentDocumentStore.name === doc.name
                            ? {
                                  ...doc,
                                  classLabels: foundLabels,
                                  documentDataset: foundLabels.length > 0 ? doc.documentDataset || 'Train' : null
                              }
                            : doc
                    )
                );
            } else {
                rootStore.currentCustomTextProjectStore.setProjectDocuments([
                    ...documents,
                    { ...currentDocumentStore, classLabels: foundLabels, documentDataset: currentDocumentStore.documentDataset || 'Train' }
                ]);
            }
            _addDirtyState();
        });
    };

    const changeDocumentCulture = async ({ culture }: HandlerParams<{ culture: string }>) => {
        const { currentDocumentStore, documents } = rootStore.currentCustomTextProjectStore;
        transaction(() => {
            currentDocumentStore.setCulture(culture);
            rootStore.currentCustomTextProjectStore.setProjectDocuments(
                documents.map(d => (d.name === currentDocumentStore.name ? { ...d, language: culture } : d))
            );
            _addDirtyState();
        });
    };

    const changeCurrentDocumentStoreDataset = async ({ dataset }: HandlerParams<{ dataset: DatasetType }>) => {
        const { currentDocumentStore, documents } = rootStore.currentCustomTextProjectStore;

        transaction(() => {
            currentDocumentStore.setDocumentDataSet(dataset);
            rootStore.currentCustomTextProjectStore.setProjectDocuments(
                documents.map(d => (d.name === currentDocumentStore.name ? { ...d, documentDataset: dataset } : d))
            );
            _addDirtyState();
        });
    };

    const saveDocumentStoresChanges = async ({}: HandlerParams<{}>) => {
        const { documents } = rootStore.currentCustomTextProjectStore;

        const updatedDocuments = documents.map(documentStoreToDocumentMetadata);

        rootStore.currentCustomTextProjectStore.setProjectDocuments(updatedDocuments);
    };

    const switchCurrentDocumentStore = async ({ newDocumentName }: HandlerParams<{ newDocumentName: string }>) => {
        const { documents } = rootStore.currentCustomTextProjectStore;

        const newDocumentStoreMetadata = documents.find(doc => doc.name === newDocumentName);

        rootStore.currentCustomTextProjectStore.setCurrentDocumentStore(documentMetadataToDocumentStore(newDocumentStoreMetadata));
    };

    const setDocumentsDataset = async ({ documentNames, dataset }: HandlerParams<{ documentNames: string[]; dataset: DatasetType }>) => {
        const { documents } = rootStore.currentCustomTextProjectStore;

        const updatedDocuments = documents.map(doc => (documentNames.includes(doc.name) ? { ...doc, documentDataset: dataset } : doc));

        transaction(() => {
            rootStore.currentCustomTextProjectStore.setProjectDocuments(updatedDocuments);
            _addDirtyState('documents');
        });
    };

    const closeCurrentDocumentStore = async () => {
        /**
         * This might be null if the project is closed. See this issue:
         * @see https://github.com/facebook/react/issues/16728
         */
        rootStore.currentCustomTextProjectStore?.setCurrentDocumentStore(null);
    };

    const rejectGptLabels = async ({ labels }: HandlerParams<{ labels: PartialEntityLabel[] }>) => {
        const { gptPredictionsStore, currentDocumentStore } = rootStore.currentCustomTextProjectStore;
        const gptPredictedDocumentData = gptPredictionsStore.documents.find(d => d.name === currentDocumentStore?.name);

        gptPredictionsStore.setDocumentRejectedEntityLabels(currentDocumentStore.name, [
            ...gptPredictedDocumentData.rejectedEntityLabels,
            ...labels.map(l => ({ ...l, labelId: getLabelId(l) }))
        ]);
        gptPredictedDocumentData.setEntityLabels(
            gptPredictedDocumentData.entityLabels.filter(
                gptLabel =>
                    !labels.some(
                        l =>
                            gptLabel.endCharIndex === l.endCharIndex &&
                            gptLabel.entityId === l.entityId &&
                            gptLabel.startCharIndex === l.startCharIndex
                    )
            )
        );
    };

    const submitGptLabels = async ({
        labels,
        labelingHistoryStacks
    }: HandlerParams<{ labels: PartialEntityLabel[]; labelingHistoryStacks: HistoryStacks<CustomTextEntityLabelStore[]> }>) => {
        const { gptPredictionsStore, currentDocumentStore } = rootStore.currentCustomTextProjectStore;
        const gptPredictedDocumentData = gptPredictionsStore.documents.find(d => d.name === currentDocumentStore?.name);

        gptPredictionsStore.setDocumentAcceptedEntityLabels(currentDocumentStore.name, [
            ...gptPredictedDocumentData.acceptedEntityLabels,
            ...labels.map(l => ({ ...l, labelId: getLabelId(l) }))
        ]);
        gptPredictedDocumentData.setEntityLabels(
            gptPredictedDocumentData.entityLabels.filter(
                gptLabel =>
                    !labels.some(
                        l =>
                            gptLabel.endCharIndex === l.endCharIndex &&
                            gptLabel.entityId === l.entityId &&
                            gptLabel.startCharIndex === l.startCharIndex
                    )
            )
        );
        labels.map(label =>
            submitExtractionLabels({
                markDirty: false,
                labelingHistoryStacks,
                action: {
                    name: 'AddLabel',
                    label: { start: label.startCharIndex, end: label.endCharIndex, entityId: label.entityId }
                }
            })
        );
    };

    const submitGptClassificationLabels = async ({ classIds }: HandlerParams<{ classIds: string[] }>) => {
        const { gptPredictionsStore, currentDocumentStore } = rootStore.currentCustomTextProjectStore;
        const gptPredictedDocumentData = gptPredictionsStore.documents.find(d => d.name === currentDocumentStore?.name);

        gptPredictionsStore.setDocumentAcceptedClassLabels(currentDocumentStore.name, [
            ...gptPredictedDocumentData.acceptedClassLabels,
            ...classIds.map(classId => gptPredictedDocumentData.classLabels.find(c => c.id === classId))
        ]);
        gptPredictedDocumentData.setClassLabels(
            gptPredictedDocumentData.classLabels.filter(gptLabel => !classIds.some(c => gptLabel.id === c))
        );
        submitCurrentDocumentClassificationClasses({ classIds });
    };

    const rejectGptClassificationLabels = async ({ classIds }: HandlerParams<{ classIds: string[] }>) => {
        const { gptPredictionsStore, currentDocumentStore } = rootStore.currentCustomTextProjectStore;
        const gptPredictedDocumentData = gptPredictionsStore.documents.find(d => d.name === currentDocumentStore?.name);

        gptPredictionsStore.setDocumentRejectedClassLabels(currentDocumentStore.name, [
            ...gptPredictedDocumentData.rejectedClassLabels,
            ...classIds.map(classId => gptPredictedDocumentData.classLabels.find(c => c.id === classId))
        ]);
        gptPredictedDocumentData.setClassLabels(
            gptPredictedDocumentData.classLabels.filter(gptLabel => !classIds.some(c => gptLabel.id === c))
        );
    };

    return {
        rejectGptLabels,
        submitGptLabels,
        setDocumentsDataset,
        undoExtractionLabels,
        redoExtractionLabels,
        changeDocumentCulture,
        addNewExtractionEntity,
        submitExtractionLabels,
        removeExtractionEntities,
        addNewClassificationClass,
        closeCurrentDocumentStore,
        saveDocumentStoresChanges,
        fetchDocumentContentByName,
        switchCurrentDocumentStore,
        removeClassificationClasses,
        submitClassificationClasses,
        submitGptClassificationLabels,
        rejectGptClassificationLabels,
        changeCurrentDocumentStoreDataset,
        submitCurrentDocumentClassificationClasses
    };
};
